/* Copyright (c) 2017 Backpack Health LLC */

/* global window */
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ReadOnlyShareCardHeaderTitle } from '../components/ShareCardHeaderTitle';

import { IJewelryProvider } from '../../../../models/JewelryProvider';

import TextInput, { NumberInput } from '../../../components/TextInput';
import Spacer from '../../../components/Spacer';
import { isWholeNumber } from '../../../../utilities/Validators';
import ShareCardTemplate from '../components/ShareCardTemplate';
import { Markdown } from '../../../components/Markdown';
import ContentLoader from '../../../components/ContentLoader';

import { useMedicalJewelry } from '../../../hooks/useMedicalJewelry';
import { useUIState } from '../../../hooks/useUIState';

export interface Props {
  shareCardId: number;
  provider: IJewelryProvider;
  shareCardColorModel: any;
  onSubmitSuccessful(): void;
  onCancel(): void;
}

function isOrderNumberValid(orderValue) {
  return isWholeNumber(orderValue) && orderValue.length > 4;
}

function isIdValid(idValue) {
  return idValue.length === 6;
}
const RegisterJewelryStickyJ: React.FC<Props> = (props) => {
  const { shareCardColorModel, onCancel, provider, shareCardId, onSubmitSuccessful } = props;
  const { help_messaging, company_name } = provider;

  const [, jewelryProviderActions] = useMedicalJewelry();
  const [, uiActions] = useUIState();

  const [state, setState] = React.useState({
    idValue: '',
    orderValue: '',
    isSubmitting: false,
  });

  const isValid = isIdValid(state.idValue) && isOrderNumberValid(state.orderValue);

  return (
    <ShareCardTemplate
      shareCardColorModel={shareCardColorModel}
      disableScrolling={false}
      scrollAwareHeader={(
        <ReadOnlyShareCardHeaderTitle
          isGroupAccessible={false}
          cssColor={shareCardColorModel.cssColor(1)}
          title={<FormattedMessage id="registerJewelry.title.text" values={{ string1: company_name }} />}
          onCancel={onCancel}
          onSubmit={() => {
            if (!isValid) { return; }
            setState({ ...state, isSubmitting: true });

            jewelryProviderActions.apiAddToShareCard(shareCardId, state.orderValue, state.idValue)
              .then(() => {
                setState({ ...state, isSubmitting: false });
                onSubmitSuccessful();
              }).catch((apiError) => {
                setState({ ...state, isSubmitting: false });
                uiActions.showNotification(apiError.messageKey());
              });
          }}
          isSubmitDisabled={!isValid || state.isSubmitting}
        />
      )}
      shareCardFooter={null}
      isContentLoaded={true}
      shareCardId={shareCardId}
    >
      <ContentLoader isContentLoaded={!state.isSubmitting}>
        <div className="font-footnote font-color-gsMedDark bkgd-color-gsLightest" style={{ padding: '18px 24px 0' }}>
          <FormattedMessage id="registerJewelry.stickyJ.intro.text" />
        </div>

        <form className="font-subhead font-color-gsMedDark border-color-gsLight" style={{ backgroundColor: 'white', padding: '0 24px 18px', borderBottomStyle: 'solid', borderBottomWidth: 1 }}>
          <TextInput
            value={state.idValue}
            onChange={(e) => {
              setState({
                ...state,
                idValue: e.target.value.toUpperCase().substr(0, 6),
              });
            }}
            type="text"
            label={<FormattedMessage id="registerJewelry.stickyJ.baseURLID.text" />}
            regularHint={<FormattedMessage id="registerJewelry.stickyJ.hintID.text" />}
            name="stickyj_number"
            hasFixedLabel={false}
          />

          <Spacer size="12" />

          <NumberInput
            value={state.orderValue}
            onChange={(e) => {
              const value = e.target.value;
              const isValid = isWholeNumber(value) || value === '';

              setState({
                ...state,
                orderValue: isValid ? value.substr(0, 6) : state.orderValue.substr(0, 6),
              });
            }}
            label={<FormattedMessage id="registerJewelry.stickyJ.orderNumber.text" />}
            regularHint={<FormattedMessage id="registerJewelry.stickyJ.hintOrderNumber.text" />}
            name="stickyj_order"
            hasFixedLabel={false}
          />
        </form>

      <div style={{ padding: '24px' }}>
        <div className="font-headline font-color-gsMedDark" style={{ marginBottom: 14 }}>
          <FormattedMessage id="registerJewelry.support.title.text" />
        </div>

        <div>
          <Markdown id="stickyj_register" text={help_messaging} color={shareCardColorModel.cssColor(1)} />
        </div>
      </div>
      </ContentLoader>
    </ShareCardTemplate>
  );
};

export default RegisterJewelryStickyJ;
